import {
  Button,
  ConfirmModal,
  FormField,
  FormSection,
  FormSelect,
  InfoGrid,
  InfoRow,
  PrimaryButton,
} from '@/components';
import { Sidepanel } from '@/components/layout/Sidepanel';
import { StatusLabel } from '@/features/devices/components/StatusLabel';
import { formatDate } from '@/utils/dateUtils';
import { useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCreateServiceContract,
  useDeleteServiceContract,
  usePatchServiceContract,
  useUploadContractDocument,
} from '../hooks';
import {
  CONTRACT_DURATIONS,
  type ContractAction,
  type ContractStatus,
  type ServiceContract,
} from '../types';
import { ContractDocumentList } from './ContractDocumentList';
import { ContractDocumentUpload } from './ContractDocumentUpload';
import { contractStatusLabelStyles } from './ContractStatusBadge';

interface ServiceContractSidePanelProps {
  deviceId: string;
  companyId: string;
  // The current contract being managed, or null to create a new one.
  contract: ServiceContract | null;
  isOpen: boolean;
  panelKey: string | null;
  onClose: () => void;
}

// ServiceContractSidePanel hosts the contract lifecycle and keeps the sidepanel shell small.
export function ServiceContractSidePanel({
  deviceId,
  companyId,
  contract,
  isOpen,
  panelKey,
  onClose,
}: ServiceContractSidePanelProps) {
  const { t } = useTranslation();
  const title = contract
    ? t('serviceContracts.sidePanel.manageTitle')
    : t('serviceContracts.sidePanel.createTitle');

  return (
    <Sidepanel
      key={panelKey ?? undefined}
      isOpen={isOpen}
      onClose={onClose}
      title={title}
    >
      <div className="w-full p-4 sm:px-5">
        <ServiceContractSidePanelContent
          deviceId={deviceId}
          companyId={companyId}
          contract={contract}
          onDone={onClose}
        />
      </div>
    </Sidepanel>
  );
}

function ServiceContractSidePanelContent({
  deviceId,
  companyId,
  contract,
  onDone,
}: {
  deviceId: string;
  companyId: string;
  contract: ServiceContract | null;
  onDone: () => void;
}) {
  if (contract) {
    return (
      <ManageContractView
        deviceId={deviceId}
        contract={contract}
        onDone={onDone}
      />
    );
  }

  return (
    <CreateContractView
      deviceId={deviceId}
      companyId={companyId}
      onDone={onDone}
    />
  );
}

// CreateContractView collects contract data and optionally uploads the first document.
function CreateContractView({
  deviceId,
  companyId,
  onDone,
}: {
  deviceId: string;
  companyId: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const createContract = useCreateServiceContract(deviceId);
  const uploadDocument = useUploadContractDocument(deviceId, undefined);

  const [duration, setDuration] = useState<number>(CONTRACT_DURATIONS[0]);
  const [signedAt, setSignedAt] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const isSubmitting = createContract.isPending || uploadDocument.isPending;
  const canSubmit = Boolean(signedAt) && !isSubmitting;

  const handleSubmit = async () => {
    const created = await createContract.mutateAsync({
      deviceId,
      companyId,
      duration,
      signedAt,
    });
    if (file) {
      await uploadDocument.mutateAsync({ file, targetContractId: created.id });
    }
    onDone();
  };

  return (
    <div className="space-y-5">
      <FormSection icon="file" title={t('serviceContracts.sections.contract')}>
        <ContractDurationSelect value={duration} onChange={setDuration} />
        <FormField
          label={t('serviceContracts.fields.signedAt')}
          type="date"
          value={signedAt}
          onChange={(event) => setSignedAt(event.target.value)}
          mandatory
        />
      </FormSection>

      <FormSection
        icon="documents"
        title={t('serviceContracts.sections.documents')}
      >
        <span className="text-onsurface text-sm">
          {t('serviceContracts.documents.optionalLabel')}
        </span>
        <ContractDocumentUpload
          file={file}
          onFileSelect={setFile}
          disabled={isSubmitting}
        />
      </FormSection>

      <PrimaryButton
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        isLoading={isSubmitting}
        fullWidth
      >
        {t('serviceContracts.actions.create')}
      </PrimaryButton>
    </div>
  );
}

// A pending confirmation is either a lifecycle transition (PATCH) or the permanent delete (DELETE).
type PendingContractAction = ContractAction | 'delete';

// ManageContractView organizes editing into FormSection groups and owns lifecycle mutations.
function ManageContractView({
  deviceId,
  contract,
  onDone,
}: {
  deviceId: string;
  contract: ServiceContract;
  onDone: () => void;
}) {
  const patchContract = usePatchServiceContract(deviceId);
  const deleteContract = useDeleteServiceContract(deviceId);
  const uploadDocument = useUploadContractDocument(deviceId, contract.id);

  const [pendingAction, setPendingAction] =
    useState<PendingContractAction | null>(null);
  const [startDate, setStartDate] = useState('');
  const [duration, setDuration] = useState<number>(
    contract.duration || CONTRACT_DURATIONS[0]
  );
  const [file, setFile] = useState<File | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  const canUploadDocuments = contract.status === 'Active';

  // Delete is a permanent DELETE and closes the panel; every other action is a
  // lifecycle PATCH, with activation alone sending editable start data.
  const handleConfirm = () => {
    if (!pendingAction) return;

    if (pendingAction === 'delete') {
      deleteContract.mutate(contract.id, {
        onSuccess: () => {
          setPendingAction(null);
          onDone();
        },
        onError: () => setPendingAction(null),
      });
      return;
    }

    const request =
      pendingAction === 'activate'
        ? { action: pendingAction, signedAt: startDate, duration }
        : { action: pendingAction };

    patchContract.mutate(
      { contractId: contract.id, request },
      {
        onSuccess: () => {
          setPendingAction(null);
        },
        onError: () => setPendingAction(null),
      }
    );
  };

  const handleUpload = () => {
    if (!file) return;
    uploadDocument.mutate(
      { file, targetContractId: contract.id },
      { onSuccess: () => setFile(null) }
    );
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!canUploadDocuments || !hasDraggedFiles(event)) return;
    event.preventDefault();
    setIsDraggingFile(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget))
      return;
    setIsDraggingFile(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!canUploadDocuments || !hasDraggedFiles(event)) return;
    event.preventDefault();
    setIsDraggingFile(false);
    setFile(event.dataTransfer.files[0] ?? null);
  };

  return (
    <div
      className="space-y-5"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <ContractInformationSection contract={contract} />
      <ContractActionsSection
        status={contract.status}
        startDate={startDate}
        duration={duration}
        onStartDateChange={setStartDate}
        onDurationChange={setDuration}
        onAction={setPendingAction}
      />
      <ContractDocumentsSection
        deviceId={deviceId}
        contract={contract}
        file={file}
        showDropzone={isDraggingFile}
        isUploading={uploadDocument.isPending}
        onFileSelect={setFile}
        onUpload={handleUpload}
      />
      <ContractActionConfirmations
        pendingAction={pendingAction}
        isLoading={patchContract.isPending || deleteContract.isPending}
        onConfirm={handleConfirm}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  );
}

function ContractInformationSection({
  contract,
}: {
  contract: ServiceContract;
}) {
  const { t } = useTranslation();

  return (
    <FormSection icon="file" title={t('serviceContracts.sections.contract')}>
      <InfoGrid>
        <InfoRow label={t('serviceContracts.fields.status')}>
          <StatusLabel
            status={contract.status}
            translationPrefix="serviceContracts.status"
            styles={contractStatusLabelStyles}
          />
        </InfoRow>
        <InfoRow label={t('serviceContracts.fields.requestedOn')}>
          {formatDate(contract.createdAt)}
        </InfoRow>
        <InfoRow label={t('serviceContracts.fields.signedAt')}>
          {formatDate(contract.signedOn ?? undefined)}
        </InfoRow>
        <InfoRow label={t('serviceContracts.fields.duration')}>
          {t('serviceContracts.fields.durationMonths', {
            months: contract.duration,
          })}
        </InfoRow>
        <InfoRow label={t('serviceContracts.fields.expiresOn')}>
          {formatDate(contract.expiresOn ?? undefined)}
        </InfoRow>
      </InfoGrid>
    </FormSection>
  );
}

function ContractActionsSection({
  status,
  startDate,
  duration,
  onStartDateChange,
  onDurationChange,
  onAction,
}: {
  status: ContractStatus;
  startDate: string;
  duration: number;
  onStartDateChange: (value: string) => void;
  onDurationChange: (value: number) => void;
  onAction: (action: PendingContractAction) => void;
}) {
  const { t } = useTranslation();
  const isRequested = status === 'Requested';

  return (
    <FormSection icon="settings" title={t('serviceContracts.sections.actions')}>
      {isRequested && (
        <RequestedContractActivationFields
          startDate={startDate}
          duration={duration}
          onStartDateChange={onStartDateChange}
          onDurationChange={onDurationChange}
        />
      )}
      <ContractActionButtons
        status={status}
        startDate={startDate}
        onAction={onAction}
      />
    </FormSection>
  );
}

function RequestedContractActivationFields({
  startDate,
  duration,
  onStartDateChange,
  onDurationChange,
}: {
  startDate: string;
  duration: number;
  onStartDateChange: (value: string) => void;
  onDurationChange: (value: number) => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      <p className="text-muted text-sm">
        {t('serviceContracts.sidePanel.requestedHint')}
      </p>
      <ContractDurationSelect value={duration} onChange={onDurationChange} />
      <FormField
        label={t('serviceContracts.fields.startDate')}
        type="date"
        value={startDate}
        onChange={(event) => onStartDateChange(event.target.value)}
        mandatory
      />
    </>
  );
}

// Lifecycle buttons sit left, the permanent delete sits right; without any lifecycle
// action available (Canceled/Expired contracts), a note replaces them on the left.
function ContractActionButtons({
  status,
  startDate,
  onAction,
}: {
  status: ContractStatus;
  startDate: string;
  onAction: (action: PendingContractAction) => void;
}) {
  const { t } = useTranslation();
  const actions = getAvailableActions(status);
  const hasLifecycleActions = actions.length > 0;

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      {hasLifecycleActions ? (
        <div className="flex flex-wrap gap-2">
          {actions.map((action) => (
            <Button
              key={action}
              type="button"
              onClick={() => onAction(action)}
              disabled={action === 'activate' && !startDate}
            >
              {t(`serviceContracts.actions.${action}`)}
            </Button>
          ))}
        </div>
      ) : (
        <p className="text-muted text-sm">
          {t('serviceContracts.sidePanel.canceledHint')}
        </p>
      )}
      <Button
        type="button"
        variant='destructive'
        onClick={() => onAction('delete')}
      >
        {t('serviceContracts.actions.delete')}
      </Button>
    </div>
  );
}

function ContractDocumentsSection({
  deviceId,
  contract,
  file,
  showDropzone,
  isUploading,
  onFileSelect,
  onUpload,
}: {
  deviceId: string;
  contract: ServiceContract;
  file: File | null;
  showDropzone: boolean;
  isUploading: boolean;
  onFileSelect: (file: File | null) => void;
  onUpload: () => void;
}) {
  const { t } = useTranslation();
  const canUploadDocuments = contract.status === 'Active';

  return (
    <FormSection
      icon="documents"
      title={t('serviceContracts.sections.documents')}
    >
      <ContractDocumentList deviceId={deviceId} contractId={contract.id} />
      {canUploadDocuments && (
        <ContractDocumentUpload
          file={file}
          onFileSelect={onFileSelect}
          onUpload={onUpload}
          isUploading={isUploading}
          collapsed
          showDropzone={showDropzone}
        />
      )}
    </FormSection>
  );
}

function ContractActionConfirmations({
  pendingAction,
  isLoading,
  onConfirm,
  onCancel,
}: {
  pendingAction: PendingContractAction | null;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();

  if (!pendingAction) {
    return null;
  }

  const isDangerous = pendingAction === 'cancel' || pendingAction === 'delete';

  return (
    <ConfirmModal
      isOpen
      title={t(`serviceContracts.confirm.${pendingAction}.title`)}
      message={t(`serviceContracts.confirm.${pendingAction}.message`)}
      confirmLabel={t('common.confirm')}
      cancelLabel={t('common.cancel')}
      onConfirm={onConfirm}
      onCancel={onCancel}
      isLoading={isLoading}
      variant={isDangerous ? 'danger' : 'default'}
    />
  );
}

function ContractDurationSelect({
  value,
  onChange,
}: {
  value: number;
  onChange: (value: number) => void;
}) {
  const { t } = useTranslation();

  return (
    <FormSelect
      label={t('serviceContracts.fields.duration')}
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
    >
      {CONTRACT_DURATIONS.map((months) => (
        <option key={months} value={months}>
          {t('serviceContracts.fields.durationMonths', { months })}
        </option>
      ))}
    </FormSelect>
  );
}

function getAvailableActions(status: ContractStatus): ContractAction[] {
  if (status === 'Requested') {
    return ['activate', 'cancel'];
  }
  if (status === 'Active') {
    return ['lock', 'cancel'];
  }
  if (status === 'Locked') {
    return ['unlock', 'cancel'];
  }

  return [];
}

function hasDraggedFiles(event: DragEvent<HTMLDivElement>): boolean {
  return Array.from(event.dataTransfer.types).includes('Files');
}
